import React, { useState, useMemo } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup } from "reactstrap"
import { useMutation, gql, useQuery } from "@apollo/client"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import "./CommsNewConversationModal.css"
import { GET_ALL_CHANNELS, GET_MESSAGE_TEMPLATES, GET_DRIVER_CONTEXT } from "./Queries"
import { resolveTemplateVariables, buildTemplateContext } from "./templateVariables"
import { DriverTypeAhead } from "../TaskManagement/TaskComponents/TypeAheadComponents"
import CannedResponsePicker from "./SendMessage/components/CannedResponsePicker"
import { useNavigate } from "react-router-dom"

const UPSERT_CONVERSATION_MESSAGE = gql`
  mutation MessagingUpsertConversationMessage(
    $to: String
    $driverId: ID
    $body: String!
    $bodyHtml: String
    $channelId: ID
    $subject: String
    $conversationId: ID
  ) {
    messagingUpsertConversationMessage(
      to: $to
      driverId: $driverId
      body: $body
      bodyHtml: $bodyHtml
      channelId: $channelId
      subject: $subject
      conversationId: $conversationId
    ) {
      ok
      errors
      conversation {
        id
      }
      message {
        id
      }
    }
  }
`

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
}

const htmlToPlainText = (htmlValue = "") => {
  if (!htmlValue) return ""
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlValue, "text/html")
  const blockTags = new Set(["DIV", "P", "LI", "TR", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "TABLE"])
  let text = ""

  const walk = (node) => {
    if (!node) return
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.nodeValue || ""
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const tagName = node.tagName
    if (tagName === "BR") { text += "\n"; return }
    const isBlock = blockTags.has(tagName)
    if (isBlock && text && !text.endsWith("\n")) text += "\n"
    Array.from(node.childNodes).forEach(walk)
    if (isBlock && !text.endsWith("\n")) text += "\n"
  }

  Array.from(doc.body?.childNodes || []).forEach(walk)
  return text
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

const plainTextToHtml = (textValue = "") => {
  if (!textValue) return ""
  return textValue.split("\n").map(line => `<p>${line || "<br />"}</p>`).join("")
}

const emptyForm = {
  to: "",
  driverId: "",
  subject: "",
  body: "",
  bodyHtml: "",
  channelId: "",
}

export default function CommsNewConversationModal({ isOpen, toggle }) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState(emptyForm)
  const [recipientType, setRecipientType] = useState("driver")
  const [isTemplatePickerOpen, setTemplatePickerOpen] = useState(false)
  const [error, setError] = useState("")

  const [sendMutation, { loading }] = useMutation(UPSERT_CONVERSATION_MESSAGE)

  const { data: channelsData, loading: loadingChannels } = useQuery(GET_ALL_CHANNELS, {
    variables: { first: 100 },
    fetchPolicy: "cache-and-network",
  })
  const channels = channelsData?.allChannels?.edges.map(edge => edge.node) || []

  const { data: templateData } = useQuery(GET_MESSAGE_TEMPLATES, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  })
  const templates = useMemo(
    () => templateData?.allMessageTemplates?.edges?.map(edge => edge.node) || [],
    [templateData]
  )

  const { data: driverContextData } = useQuery(GET_DRIVER_CONTEXT, {
    variables: { id: formData.driverId },
    skip: !formData.driverId,
    fetchPolicy: "cache-first",
  })

  const selectedChannel = useMemo(
    () => channels.find(c => c.id === formData.channelId) || null,
    [channels, formData.channelId]
  )
  const isEmailChannel = selectedChannel?.channelType === "EMAIL"

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleEmailEditorChange = (nextHtml) => {
    setFormData(prev => ({
      ...prev,
      bodyHtml: nextHtml || "",
      body: htmlToPlainText(nextHtml || ""),
    }))
  }

  const handleTemplateSelect = (template) => {
    const ctx = buildTemplateContext(driverContextData?.driver)
    if (isEmailChannel) {
      const rawHtml = template.contentHtml || plainTextToHtml(template.content || "")
      const html = resolveTemplateVariables(rawHtml, ctx)
      const plain = resolveTemplateVariables(htmlToPlainText(html) || template.content || "", ctx)
      setFormData(prev => ({
        ...prev,
        bodyHtml: html,
        body: plain,
        subject: prev.subject ? prev.subject : template.title,
      }))
    } else {
      const rawPlain = template.content || htmlToPlainText(template.contentHtml || "")
      const plain = resolveTemplateVariables(rawPlain, ctx)
      setFormData(prev => ({ ...prev, body: plain }))
    }
    setTemplatePickerOpen(false)
  }

  const handleRecipientTypeToggle = (type) => {
    setRecipientType(type)
    setFormData(prev => ({ ...prev, driverId: "", to: "" }))
  }

  const handleSend = async () => {
    try {
      const variables = {
        to: formData.to || null,
        driverId: formData.driverId || null,
        body: formData.body,
        bodyHtml: isEmailChannel && formData.bodyHtml ? formData.bodyHtml : null,
        subject: formData.subject || null,
        channelId: formData.channelId || null,
      }

      const { data } = await sendMutation({ variables })
      if (data?.messagingUpsertConversationMessage?.ok) {
        const newConversationId = data.messagingUpsertConversationMessage.conversation?.id
        toggle()
        setFormData(emptyForm)
        setRecipientType("driver")
        setError("")
        navigate(`/comms/conversation/${newConversationId}/`)
      } else {
        setError(data?.messagingUpsertConversationMessage?.errors?.toString() || "An unknown error occurred")
      }
    } catch (err) {
      setError(err.toString())
    }
  }

  const sendDisabled = loading || !formData.body || !formData.channelId || (!formData.driverId && !formData.to)

  return (
    <Modal isOpen={isOpen} centered size="lg" className="CommsNewConversationModal">
      <ModalHeader>Start a New Conversation</ModalHeader>
      <ModalBody>
        {error && <div className="alert alert-danger">Error: {error}</div>}

        <FormGroup>
          <Label for="channelId">Channel</Label>
          <Input
            type="select"
            name="channelId"
            value={formData.channelId}
            disabled={loadingChannels}
            onChange={handleChange}
          >
            <option value="">{loadingChannels ? "Loading channels..." : "Select a channel"}</option>
            {channels.map((channel, idx) => (
              <option key={idx} value={channel.id}>
                {channel.name} ({channel.channelType})
              </option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>Recipient</Label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <Button
              size="sm"
              color={recipientType === "driver" ? "primary" : "light"}
              onClick={() => handleRecipientTypeToggle("driver")}
            >
              Driver
            </Button>
            <Button
              size="sm"
              color={recipientType === "manual" ? "primary" : "light"}
              onClick={() => handleRecipientTypeToggle("manual")}
            >
              Contact (manual)
            </Button>
          </div>
          {recipientType === "driver" ? (
            <DriverTypeAhead
              key="driver-typeahead"
              onChange={(driverId) => setFormData(prev => ({ ...prev, driverId, to: "" }))}
            />
          ) : (
            <Input
              type="text"
              name="to"
              value={formData.to}
              onChange={handleChange}
              placeholder="Email or phone number"
            />
          )}
        </FormGroup>

        <FormGroup>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <Label style={{ margin: 0 }}>Template</Label>
            <div style={{ position: "relative" }}>
              <Button
                type="button"
                color="light"
                size="sm"
                onClick={() => setTemplatePickerOpen(!isTemplatePickerOpen)}
                style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
              >
                <i className="fa fa-bookmark" aria-hidden="true"></i>
                <span>Choose template</span>
              </Button>
              {isTemplatePickerOpen && (
                <CannedResponsePicker
                  templates={templates}
                  onSelect={handleTemplateSelect}
                  onClose={() => setTemplatePickerOpen(false)}
                  isOpen={isTemplatePickerOpen}
                />
              )}
            </div>
          </div>
        </FormGroup>

        {isEmailChannel && (
          <FormGroup>
            <Label for="subject">Subject</Label>
            <Input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label style={{ margin: 0 }}>Message</Label>
          <div style={{ marginTop: "6px" }}>
            {isEmailChannel ? (
              <div className="comms-send-rich-editor">
                <ReactQuill
                  theme="snow"
                  value={formData.bodyHtml || plainTextToHtml(formData.body)}
                  onChange={handleEmailEditorChange}
                  modules={quillModules}
                  placeholder="Type an email..."
                />
              </div>
            ) : (
              <Input
                type="textarea"
                name="body"
                rows="4"
                value={formData.body}
                onChange={handleChange}
                placeholder="Type your message..."
              />
            )}
          </div>
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>Cancel</Button>
        <Button
          color="primary"
          onClick={handleSend}
          disabled={sendDisabled}
        >
          {loading ? "Sending..." : "Send"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
